import { z } from 'zod';
import { uploadedFileSchema } from '@terra/shared-authorization';
import {
  AssetReferenceSchema,
  AssetTypeSchema,
  ExternalReferenceSchema,
  FindingSeveritySchema,
  FindingStatusSchema,
  OwaspRiskDataSchema,
  POC_IMAGE_CAPTION_MAX_LENGTH,
  POC_IMAGE_MAX_SIZE_BYTES,
  POC_IMAGE_MIME_TYPES,
  PocBlocksSchema,
  RemediationPrLinkReasonSchema,
  SignalCategorySchema,
  TargetTypeSchema,
  InfraImpactSchema,
  ExternalNetworkEntityKindSchema,
} from '@terra/shared-schemas-orchestrator';
import { TraceStatusSchema } from '@terra/shared-schemas-ai';
import {
  AssetLifecycleDtoSchema,
  LifecycleStateFilterSchema,
  OptionalBooleanQueryParamSchema,
  PaginationDefaults,
  PaginationQueryParamsSchema,
} from '@terra/shared-dto-types';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import { BaseAppFindingDtoSchema } from './finding-base.schema';
import { FindingCvssDataSchema } from './finding-create.schema';
import { FindingsThenSortQueryShape } from './findings-then-sort.schema';
import { FindingCvssVersionUpdateFieldsSchema } from './finding-cvss-versions.schema';
import { FindingTypeFieldsSchema } from './finding-type.schema';
import { SeverityChangeRequestSchema } from './severity-change-request.schemas';
import {
  FINDINGS_GROUPED_LIST_LIMIT,
  FindingsGroupByFieldSchema,
  FindingsGroupCountSchema,
} from './findings-group-by.schema';

// Customer-facing finding statuses (excludes internal-only workflow statuses)
export const CustomerFindingStatusSchema = z
  .enum([
    'Resolved',
    'Unresolved',
    'InProgress',
    'FalsePositive',
    'WontFix',
    'Retest',
  ])
  .describe('Status values that customers can set for findings');

export const BulkCustomerFindingStatusSchema =
  CustomerFindingStatusSchema.exclude(['Retest']).describe(
    'Status values allowed for bulk finding updates (excludes Retest)',
  );

const BulkFindingUpdateFieldsSchema = z.object({
  status: BulkCustomerFindingStatusSchema.optional(),
  assignedTo: z.string().optional(),
  severity: FindingSeveritySchema.optional(),
  isPublished: z.boolean().optional(),
});

export const FindingStatsSchema = z
  .object({
    total: z.number().describe('Total number of findings'),
    critical: z.number().describe('Number of critical severity findings'),
    high: z.number().describe('Number of high severity findings'),
    medium: z.number().describe('Number of medium severity findings'),
    low: z.number().describe('Number of low severity findings'),
  })
  .describe('Aggregated finding statistics by severity');

export const FindingsBySeveritySchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  info: z.number(),
});

export const FindingsByStatusSchema = z.object({
  unresolved: z.number(),
  inProgress: z.number(),
  retest: z.number(),
  resolved: z.number(),
  falsePositive: z.number(),
  wontFix: z.number(),
});

export const FindingsMetricSchema = z
  .object({
    total: z.number().describe('Total findings count'),
    bySeverity: FindingsBySeveritySchema.optional(),
    byStatus: FindingsByStatusSchema.optional(),
  })
  .describe(
    'Findings metrics with optional severity and status facets for a single request',
  );

export const FindingRemediationPrDtoSchema = z
  .object({
    prUrl: z.string(),
    prNumber: z.number(),
    prBranch: z.string().nullable(),
    prTitle: z.string().nullable(),
    linkReason: RemediationPrLinkReasonSchema,
    createdAt: z.coerce.date(),
  })
  .nullable()
  .default(null);

export const FindingTargetFilterQuerySchema = z.object({
  targetType: z
    .union([TargetTypeSchema, z.array(TargetTypeSchema)])
    .optional()
    .describe('Filter findings by one or more target types'),
  targetScope: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .describe(
      'Filter findings by target scope keys in the form TARGET_TYPE:targetId',
    ),
});

export const FindingStatsQuerySchema = z
  .object({
    createdStartDate: z.coerce.date().optional(),
  })
  .extend(FindingTargetFilterQuerySchema.shape);

export const FindingFilterQuerySchema = z.object({
  severity: z
    .union([FindingSeveritySchema, z.array(FindingSeveritySchema)])
    .optional(),
  status: FindingStatusSchema.optional(),
  createdAtFrom: z.coerce
    .date()
    .optional()
    .describe('Filter findings published on or after this date'),
  createdAtTo: z.coerce
    .date()
    .optional()
    .describe('Filter findings published on or before this date'),
  hasPrevention: OptionalBooleanQueryParamSchema.describe(
    'Filter findings by whether they have prevention rules',
  ),
  hasPendingSeverityChangeRequest: OptionalBooleanQueryParamSchema.describe(
    'Filter findings by whether they have a pending severity change request',
  ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
  ...FindingTargetFilterQuerySchema.shape,
});

export const FindingRiskScoreSchema = z.object({
  riskScore: z.number(),
});

export const FindingOverTimeSchema = z.object({
  critical: z.array(z.number()),
  high: z.array(z.number()),
  medium: z.array(z.number()),
  low: z.array(z.number()),
  months: z.array(z.string()),
});

export const FindingStatusSchemaResponse = z.object({
  status: FindingStatusSchema,
  count: z.number(),
});

// Dashboard Analytics Schema
export const FindingStatusDistributionSchema = z.object({
  unresolved: z.number(),
  resolved: z.number(),
  falsePositive: z.number(),
  wontFix: z.number(),
  inProgress: z.number(),
  retest: z.number(),
});

// Base finding schema with common fields for all endpoints. Wire shape is
// hand-rolled and decoupled from the generated Prisma schema; see
// `finding-base.schema.ts`.
const BaseFindingSchema = BaseAppFindingDtoSchema;

// List DTO - minimal fields for list views (no encrypted content)
export const FindingListDtoSchema = BaseFindingSchema.extend(
  z.object({
    cvss: z.lazy(() => FindingCvssDataSchema).nullable(),
    owaspRisk: z.lazy(() => OwaspRiskDataSchema).nullable(),
    assets: z.array(z.lazy(() => AssetReferenceSchema)),
    externalReferences: z.array(ExternalReferenceSchema).default([]),
    isPublished: z.boolean().nullable(),
  }).shape,
);

// Detail DTO - includes all decrypted content fields for detail views
export const FindingDetailDtoSchema = BaseFindingSchema.extend(
  z.object({
    severityChangeRequest: SeverityChangeRequestSchema.nullable().optional(),

    // Relations
    cvss: z.lazy(() => FindingCvssDataSchema).nullable(),
    owaspRisk: z.lazy(() => OwaspRiskDataSchema).nullable(),
    assets: z.array(z.lazy(() => AssetReferenceSchema)).default([]),
    externalReferences: z.array(ExternalReferenceSchema),

    // Decrypted content fields (available in detail views)
    description: z.string().nullable(),
    poc: z.string().nullable(),
    pocBlocks: PocBlocksSchema.nullable().default(null),
    businessImpact: z.string().nullable(),
    attackCapabilities: z.string().nullable(),
    mitigationPlan: z.string().nullable(),
    findingReport: z.string().nullable(),
    exploitationGuide: z.string().nullable(),
    executionScript: z.string().nullable(),
    pocScript: z.string().nullable(),
    pocScriptBlockedByGuardrails: z.boolean().nullable(),
    pocScriptGuardrailsFeedback: z.string().nullable(),
    remediationPlan: z.string().nullable().default(null),
    remediationPlanGeneratedAt: z.iso.datetime().nullable().default(null),
    remediationPr: FindingRemediationPrDtoSchema,

    preventionStatus: z
      .enum([
        'generating',
        'staged',
        'activating',
        'active',
        'advisory',
        'rejected',
        'failed',
        'removed',
        'expired',
        'drifted',
      ])
      .nullable()
      .optional(),
    preventionControlType: z
      .enum(['waf', 'network', 'config', 'ips'])
      .nullable()
      .optional(),
    preventionControlRef: z.string().nullable().optional(),
    preventionActivatedAt: z.iso.datetime().nullable().optional(),

    // Publication status (only in detail views)
    isPublished: z.boolean().nullable(),
    wasEverRetested: z.boolean().optional(),
    scheduledRetestAt: z.coerce.date().nullable().optional(),
  }).shape,
);

// Customer update schema - only allows status changes to customer-facing statuses
export const CustomerFindingUpdateSchema = z.object({
  status: CustomerFindingStatusSchema,
  note: z.string().trim().optional(),
  disproveRelatedSignal: z.boolean().optional(),
});

// Internal update schema - SECURITY: Only allows updating user-editable fields
// Excludes: id, tenantId, applicationId, createdAt, updatedAt, discoveryDate, metadata
export const FindingUpdateInputSchema = z.object({
  title: z.string().optional(),
  shortDescription: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  poc: z.string().nullable().optional(),
  pocBlocks: PocBlocksSchema.nullable().optional(),
  businessImpact: z.string().nullable().optional(),
  attackCapabilities: z.string().nullable().optional(),
  mitigationPlan: z.string().nullable().optional(),
  findingReport: z.string().nullable().optional(),
  exploitationGuide: z.string().nullable().optional(),
  executionScript: z.string().nullable().optional(),
  pocScript: z.string().nullable().optional(),
  pocScriptBlockedByGuardrails: z.boolean().nullable().optional(),
  pocScriptGuardrailsFeedback: z.string().nullable().optional(),
  remediationPlan: z.string().nullable().optional(),
  status: FindingStatusSchema.optional(),
  note: z.string().trim().optional(),
  disproveRelatedSignal: z.boolean().optional(),
  severity: FindingSeveritySchema.optional(),
  category: SignalCategorySchema.nullable().optional(),
  cweIds: z.array(z.string()).optional(),
  owaspCategory: z.string().nullable().optional(),
  assignedTo: z.string().optional(),
  isPublished: z.boolean().nullable().optional(),
  signalId: z.string().nullable().optional(),
  executionStateId: z.string().nullable().optional(),
  traceThreadId: z.string().nullable().optional(),
  assets: z.array(z.lazy(() => AssetReferenceSchema)).optional(),
  cvss: z
    .lazy(() => FindingCvssDataSchema)
    .nullable()
    .optional(),
  ...FindingCvssVersionUpdateFieldsSchema.shape,
  owaspRisk: z
    .lazy(() => OwaspRiskDataSchema)
    .nullable()
    .optional(),
  externalReferences: z.array(ExternalReferenceSchema).optional(),
  ...FindingTypeFieldsSchema.shape,
  findingUpdateComment: z.string().nullable().optional(),
});

export const FindingEndpointAssetApiProtocolSchema = z.enum([
  'REST',
  'GraphQL',
  'SOAP',
  'XML',
  'MCP',
  'WebSocket',
  'gRPC',
]);

// Enriched asset reference with display data embedded
export const EnrichedAssetReferenceSchema = z.object({
  type: AssetTypeSchema,
  id: z.string(),
  // Display data for endpoints (null for non-endpoint assets)
  method: z.string().nullable(),
  path: z.string().nullable(),
  url: z.string().nullable(),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
  apiProtocol: FindingEndpointAssetApiProtocolSchema.nullable().optional(),
  // Display data for discovered assets (null for non-discovered-asset types)
  host: z.string().nullable().optional(),
  port: z.number().nullable().optional(),
  discoveredAssetType: z.string().nullable().optional(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
});

export const FindingListSignalSummarySchema = z
  .object({
    id: z.string().describe('Unique identifier of the related signal'),
    title: z
      .string()
      .nullable()
      .describe(
        'Human-readable signal title derived from description or security test case summary',
      ),
    status: z.string().nullable().describe('Current signal status'),
    stage: z.string().nullable().describe('Pipeline stage of the signal'),
    category: SignalCategorySchema.nullable().describe(
      'Signal category (e.g. IDOR, XSS)',
    ),
  })
  .describe(
    'Compact summary of the signal a finding originated from, suitable for list views',
  );

export const FindingListEnrichedDtoSchema = BaseFindingSchema.extend(
  z.object({
    cvss: z.lazy(() => FindingCvssDataSchema).nullable(),
    owaspRisk: z.lazy(() => OwaspRiskDataSchema).nullable(),
    assets: z.array(EnrichedAssetReferenceSchema),
    externalReferences: z.array(ExternalReferenceSchema).default([]),
    isPublished: z.boolean().nullable().optional(),
    signal: FindingListSignalSummarySchema.nullable()
      .optional()
      .describe(
        'Related signal summary for findings produced from a signal; null when the finding has no signal',
      ),
    wasEverRetested: z
      .boolean()
      .optional()
      .describe(
        'True if the finding ever transitioned out of the Retest status (audit-derived). Computed in batch for list views.',
      ),
    wasAutoPublished: z
      .boolean()
      .optional()
      .describe(
        'True if the latest PUBLISHED audit log for this finding was performed by a SYSTEM actor. Computed in batch for list views.',
      ),
    preventionStatus: z
      .enum([
        'generating',
        'staged',
        'activating',
        'active',
        'advisory',
        'rejected',
        'failed',
        'removed',
        'expired',
        'drifted',
      ])
      .nullable()
      .optional()
      .describe(
        'Denormalized prevention control status for list filtering and badges',
      ),
  }).shape,
);

export const FindingDetailEnrichedDtoSchema = BaseFindingSchema.extend(
  z.object({
    severityChangeRequest: SeverityChangeRequestSchema.nullable().optional(),

    // Relations
    cvss: z.lazy(() => FindingCvssDataSchema).nullable(),
    owaspRisk: z.lazy(() => OwaspRiskDataSchema).nullable(),
    // Enriched assets with display data embedded
    assets: z.array(EnrichedAssetReferenceSchema).default([]),
    externalReferences: z.array(ExternalReferenceSchema),

    // Decrypted content fields (available in detail views)
    description: z.string().nullable(),
    poc: z.string().nullable(),
    pocBlocks: PocBlocksSchema.nullable().default(null),
    businessImpact: z.string().nullable(),
    attackCapabilities: z.string().nullable(),
    mitigationPlan: z.string().nullable(),
    findingReport: z.string().nullable(),
    exploitationGuide: z.string().nullable(),
    executionScript: z.string().nullable(),
    pocScript: z.string().nullable(),
    pocScriptBlockedByGuardrails: z.boolean().nullable(),
    pocScriptGuardrailsFeedback: z.string().nullable(),
    remediationPlan: z.string().nullable().default(null),
    remediationPlanGeneratedAt: z.iso.datetime().nullable().default(null),
    remediationPr: FindingRemediationPrDtoSchema,
    preventionStatus: z
      .enum([
        'generating',
        'staged',
        'activating',
        'active',
        'advisory',
        'rejected',
        'failed',
        'removed',
        'expired',
        'drifted',
      ])
      .nullable()
      .optional(),
    preventionControlType: z
      .enum(['waf', 'network', 'config', 'ips'])
      .nullable()
      .optional(),
    preventionControlRef: z.string().nullable().optional(),
    preventionActivatedAt: z.iso.datetime().nullable().optional(),

    // Publication status (only in detail views)
    isPublished: z.boolean().nullable(),
  }).shape,
);

/** Single-finding enriched detail for drilldown; includes audit-derived flags not returned elsewhere */
export const FindingDetailEnrichedDrilldownDtoSchema =
  FindingDetailEnrichedDtoSchema.extend(
    z.object({
      wasEverRetested: z.boolean(),
      scheduledRetestAt: z.coerce.date().nullable(),
    }).shape,
  );

// POC script generation (orchestrator-owned endpoint)
export const GeneratePocScriptPathParamsSchema = z.object({
  applicationId: z.string(),
  targetId: z.string().optional(),
  findingId: z.string(),
});

export const GeneratePocScriptAcceptedSchema = z.object({
  message: z.string(),
  findingId: z.string(),
});

export const GeneratePocScriptErrorSchema = z.object({
  message: z.string(),
});

export const ReplacePocScriptBodySchema = z
  .string()
  .describe('POC script content to replace the existing script');

export const LinkedTicketProviderSchema = z.enum([
  'jira',
  'linear',
  'servicenow',
  'github_issues',
]);

export type LinkedTicketProvider = z.infer<typeof LinkedTicketProviderSchema>;

export const isLinkedTicketProvider = (
  value: string,
): value is LinkedTicketProvider =>
  LinkedTicketProviderSchema.safeParse(value).success;

export const ReplaceLinkedTicketBodySchema = z.object({
  provider: LinkedTicketProviderSchema,
  ticketUrl: z.string().trim().min(1),
});

export const FindingPocImagePathParamsSchema = z.object({
  id: MongoObjectIdSchema,
  imageId: MongoObjectIdSchema,
});

export const FindingPocImageUploadPathParamsSchema = z.object({
  id: MongoObjectIdSchema,
});

export const FindingPocImageUploadBodySchema = z.object({
  image: uploadedFileSchema({
    mimeTypes: POC_IMAGE_MIME_TYPES,
    maxFileSizeBytes: POC_IMAGE_MAX_SIZE_BYTES,
  }),
  caption: z.string().max(POC_IMAGE_CAPTION_MAX_LENGTH).optional(),
});

export const FindingPocImageSchema = z.object({
  imageId: MongoObjectIdSchema,
  findingId: MongoObjectIdSchema,
  applicationId: MongoObjectIdSchema,
  mimeType: z.enum(POC_IMAGE_MIME_TYPES),
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  caption: z.string().max(POC_IMAGE_CAPTION_MAX_LENGTH).optional(),
});

export const FindingPocImageUploadResponseSchema = z.object({
  image: FindingPocImageSchema,
});

export const FindingPocImageSignedUrlResponseSchema = z.object({
  imageId: MongoObjectIdSchema,
  signedUrl: z.string(),
  mimeType: z.enum(POC_IMAGE_MIME_TYPES),
  fileName: z.string(),
  caption: z.string().max(POC_IMAGE_CAPTION_MAX_LENGTH).optional(),
});

export const FindingPocImageDeleteResponseSchema = z.object({
  success: z.boolean(),
});

// Bulk operation schemas
export const BulkDeleteFindingsPayloadSchema = z.object({
  ids: z.array(z.string()),
});

export const BulkDeleteFindingsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  deletedIds: z.array(z.string()),
});

export const BulkUpdateFindingsPayloadSchema = z.object({
  ids: z.array(z.string()),
  update: BulkFindingUpdateFieldsSchema,
});

export const BulkUpdateFindingsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  updatedIds: z.array(z.string()),
});

const MAX_BULK_FILTER_EXCLUDED_IDS = 5000;

export const UNPUBLISHED_CONSTITUENTS_ERROR_CODE =
  'UNPUBLISHED_CONSTITUENTS' as const;

export const BulkFindingsFilterSchema = z.object({
  applicationId: z.string(),
  filter: z.object({
    endpointId: z.string().optional(),
    searchTerm: z.string().optional(),
    severity: z.array(FindingSeveritySchema).optional(),
    status: z.array(FindingStatusSchema).optional(),
    category: z.array(SignalCategorySchema).optional(),
    cweIds: z.array(z.string()).optional(),
    owaspCategory: z.array(z.string()).optional(),
    isPublished: z.boolean().optional(),
    wasAutoPublished: z.boolean().optional(),
    hasPrevention: z.boolean().optional(),
    hasPendingSeverityChangeRequest: z.boolean().optional(),
    createdStartDate: z.coerce.date().optional(),
    createdEndDate: z.coerce.date().optional(),
    discoveryStartDate: z.coerce.date().optional(),
    discoveryEndDate: z.coerce.date().optional(),
    lifecycleState: LifecycleStateFilterSchema.optional(),
  }),
  excludedIds: z
    .array(z.string())
    .max(MAX_BULK_FILTER_EXCLUDED_IDS)
    .default([]),
  includeUnpublished: z.boolean().optional(),
  lifecycleState: LifecycleStateFilterSchema.optional(),
});

export const BulkDeleteFindingsByFilterPayloadSchema = BulkFindingsFilterSchema;

export const BulkUpdateFindingsByFilterPayloadSchema =
  BulkFindingsFilterSchema.extend({
    update: BulkFindingUpdateFieldsSchema,
  });

export const BulkFilterTooManyMatchesResponseSchema = z.object({
  success: z.literal(false),
  reason: z.enum(['TOO_MANY_MATCHES', 'UNSUPPORTED_AUTO_PUBLISHED_FILTER']),
  matchedCount: z.number(),
  limit: z.number(),
});

export const UnpublishedConstituentsErrorSchema = z.object({
  message: z.string(),
  code: z.literal(UNPUBLISHED_CONSTITUENTS_ERROR_CODE),
  violations: z.array(
    z.object({
      chainFindingId: z.string(),
      chainTitle: z.string().nullable(),
      unpublishedConstituents: z.array(
        z.object({ id: z.string(), title: z.string().nullable() }),
      ),
    }),
  ),
});

const TimelineEndpointAssetSchema = z.object({
  type: z.literal('ENDPOINT'),
  id: z.string(),
  url: z.string().nullable(),
  method: z.string().nullable(),
  path: z.string().nullable(),
  createdAt: z.date(),
  apiProtocol: FindingEndpointAssetApiProtocolSchema.nullable(),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
});

const TimelineWebpageAssetSchema = z.object({
  type: z.literal('WEBPAGE'),
  id: z.string(),
  url: z.string().nullable(),
  createdAt: z.date(),
});

const TimelineDiscoveredAssetSchema = z.object({
  type: z.literal('DISCOVERED_ASSET'),
  id: z.string(),
  host: z.string().nullable(),
  port: z.number().nullable(),
  discoveredAssetType: z.string().nullable(),
  createdAt: z.date(),
});

const TimelineAssetSchema = z.discriminatedUnion('type', [
  TimelineEndpointAssetSchema,
  TimelineWebpageAssetSchema,
  TimelineDiscoveredAssetSchema,
]);

const TimelineExecutionSchema = z.object({
  id: z.string(),
  executionId: z.string().nullable().optional(),
  title: z.string(),
  status: TraceStatusSchema,
  createdAt: z.date(),
  threadId: z.string().nullable(),
  agentName: z.string().nullable(),
  outcome: z.string().nullable().optional(),
});

export const FindingTimelineSeverityChangeEventTypeSchema = z.enum([
  'Requested',
  'Changed',
  'Rejected',
]);

export const FindingTimelineSeverityChangeEventSchema = z.object({
  id: z.string(),
  type: FindingTimelineSeverityChangeEventTypeSchema,
  fromSeverity: FindingSeveritySchema,
  toSeverity: FindingSeveritySchema,
  actorName: z.string().nullable(),
  createdAt: z.coerce.date(),
  note: z.string().nullable().optional(),
});

export const FindingTimelineDtoSchema = z.object({
  assets: z.array(TimelineAssetSchema),
  codeChange: z
    .object({
      prNumber: z.string().nullable(),
      prTitle: z.string().nullable(),
      createdAt: z.date(),
      url: z.string().nullable(),
    })
    .nullable(),
  signal: z
    .object({
      id: z.string(),
      title: z.string(),
      createdAt: z.date(),
    })
    .nullable(),
  execution: TimelineExecutionSchema.nullable(),
  retestExecutions: z.array(TimelineExecutionSchema).default([]),
  resolvedAfterRetest: z
    .object({
      createdAt: z.date(),
      threadId: z.string().nullable(),
      actorName: z.string().nullable(),
    })
    .nullable()
    .default(null),
  severityChangeEvents: z
    .array(FindingTimelineSeverityChangeEventSchema)
    .default([]),
});

export const RemediationPrLinkFindingSummarySchema = z.object({
  findingId: z.coerce.string(),
  title: z.coerce.string(),
  severity: z.coerce.string(),
  status: z.coerce.string(),
  prUrl: z.coerce.string(),
  prNumber: z.coerce.number().finite(),
  prTitle: z
    .unknown()
    .nullish()
    .transform((value) => (value == null ? null : String(value))),
});

const SeverityCountSchema = z.object({
  currentValue: z
    .number()
    .describe('Number of findings for the requested time range'),
  previousValue: z
    .number()
    .describe('Number of findings from the previous equivalent time range'),
});

const AgeBucketSchema = z.object({
  Critical: SeverityCountSchema,
  High: SeverityCountSchema,
  Medium: SeverityCountSchema,
  Low: SeverityCountSchema,
  Info: SeverityCountSchema,
});

export const FindingsByAgeBucketsSchema = z
  .object({
    '0-7days': AgeBucketSchema,
    '8-14days': AgeBucketSchema,
    '15-30days': AgeBucketSchema,
    '30+days': AgeBucketSchema,
  })
  .describe(
    'Findings grouped by age buckets and severity with current and previous period values',
  );

export const FindingsSortBySchema = z.enum([
  'createdAt',
  'updatedAt',
  'discoveryDate',
  'publishedAt',
  'status',
  'isPublished',
  'severity',
  'title',
  'category',
  'signalId',
  'endpointId',
  'targetName',
  'assets',
  'linkedTicket',
  'classification',
  'cvss',
]);

export const FINDINGS_DISPLAY_SORT_BY = [
  'targetName',
  'assets',
  'linkedTicket',
  'classification',
  'cvss',
] as const;

export type FindingsDisplaySortBy = (typeof FINDINGS_DISPLAY_SORT_BY)[number];

export const isFindingsDisplaySortBy = (
  sortBy: z.infer<typeof FindingsSortBySchema> | undefined,
): sortBy is FindingsDisplaySortBy =>
  FINDINGS_DISPLAY_SORT_BY.some((displaySortBy) => displaySortBy === sortBy);

export const FindingsByApplicationListRequestBodySchema = z.object({
  includeUnpublished: z.coerce
    .boolean()
    .optional()
    .describe(
      'Include unpublished findings (requires Execute Tests permission)',
    ),
  signalId: z
    .string()
    .optional()
    .describe('Filter findings linked to a specific signal id'),
  findingIds: z
    .array(z.string())
    .optional()
    .describe('Filter findings by a specific list of finding IDs'),
  endpointId: z.coerce
    .string()
    .optional()
    .describe('Filter findings by a specific endpoint ID'),
  searchTerm: z.coerce
    .string()
    .optional()
    .describe('Filter findings by title search term'),
  severity: z
    .array(FindingSeveritySchema)
    .optional()
    .describe('Filter findings by severity values'),
  status: z
    .array(FindingStatusSchema)
    .optional()
    .describe('Filter findings by status values'),
  category: z
    .array(SignalCategorySchema)
    .optional()
    .describe('Filter findings by category values'),
  cweIds: z
    .array(z.string())
    .optional()
    .describe('Filter findings by CWE identifiers'),
  owaspCategory: z
    .array(z.string())
    .optional()
    .describe('Filter findings by OWASP Top 10:2025 category'),
  infraImpact: InfraImpactSchema.optional().describe(
    'Filter by infrastructure impact class',
  ),
  isPublished: z.coerce
    .boolean()
    .optional()
    .describe('Filter findings by publication status'),
  wasAutoPublished: z.coerce
    .boolean()
    .optional()
    .describe(
      'Filter findings whose latest PUBLISHED audit log was performed by a SYSTEM actor',
    ),
  hasPrevention: OptionalBooleanQueryParamSchema.describe(
    'Filter findings by whether they have prevention rules',
  ),
  hasPendingSeverityChangeRequest: OptionalBooleanQueryParamSchema.describe(
    'Filter findings by whether they have a pending severity change request',
  ),
  createdStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter findings published from this date'),
  createdEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter findings published until this date'),
  sortBy: FindingsSortBySchema.optional().describe(
    'Sort field for the findings list.',
  ),
  sortOrder: z
    .enum(['asc', 'desc'])
    .optional()
    .describe('Sort order for sortBy'),
  ...FindingsThenSortQueryShape,
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export const FindingsByApplicationDetailRequestBodySchema =
  FindingsByApplicationListRequestBodySchema.extend({
    entryPointId: z.coerce
      .string()
      .optional()
      .describe('Filter findings by a specific entry point ID'),
  });

export const FindingsByApplicationIdEnrichedQuerySchema =
  PaginationQueryParamsSchema.extend({
    endpointId: z.coerce
      .string()
      .optional()
      .describe('Filter findings by a specific endpoint ID'),
    signalId: z.coerce
      .string()
      .optional()
      .describe('Filter findings by a specific signal ID'),
    findingIds: z
      .array(z.string())
      .optional()
      .describe('Filter findings by a specific list of finding IDs'),
    searchTerm: z.coerce
      .string()
      .optional()
      .describe('Filter findings by title or ID'),
    createdStartDate: z.coerce
      .date()
      .optional()
      .describe('Filter findings published from this date'),
    createdEndDate: z.coerce
      .date()
      .optional()
      .describe('Filter findings published until this date'),
    hasPrevention: OptionalBooleanQueryParamSchema.describe(
      'Filter findings by whether they have prevention rules',
    ),
    hasPendingSeverityChangeRequest: OptionalBooleanQueryParamSchema.describe(
      'Filter findings by whether they have a pending severity change request',
    ),
    lifecycleState: LifecycleStateFilterSchema.optional().describe(
      'Filter by asset lifecycle state (active, archived, or all)',
    ),
  });

export const FindingsListQuerySchema = PaginationQueryParamsSchema.extend({
  limit: z.coerce.number().int().min(1).optional(),
  groupBy: FindingsGroupByFieldSchema.optional().describe(
    'When set, return group counters and allow a higher page limit',
  ),
  timeZone: z
    .string()
    .min(1)
    .max(64)
    .optional()
    .describe('IANA time zone used for calendar-day group keys'),
  targetId: z
    .string()
    .optional()
    .describe('Target id when the list is scoped to one target'),
  searchTerm: z.coerce
    .string()
    .optional()
    .describe('Filter findings by title or id substring'),
  assetSearch: z
    .string()
    .max(200)
    .optional()
    .describe(
      'Filter findings linked to assets whose name, path, URL, or host matches this substring',
    ),
  assetIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    )
    .describe('Filter findings linked to these asset ids'),
  includeDescendants: OptionalBooleanQueryParamSchema.describe(
    'When set with assetIds and rootEntityKind, include findings linked to descendant assets',
  ),
  rootEntityKind: ExternalNetworkEntityKindSchema.optional().describe(
    'Kind of the opened asset when includeDescendants is set',
  ),
  severity: z
    .union([FindingSeveritySchema, z.array(FindingSeveritySchema)])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    ),
  status: z
    .union([FindingStatusSchema, z.array(FindingStatusSchema)])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    ),
  category: z
    .union([SignalCategorySchema, z.array(SignalCategorySchema)])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    ),
  infraImpact: InfraImpactSchema.optional().describe(
    'Filter by infrastructure impact class',
  ),
  signalId: z.coerce
    .string()
    .optional()
    .describe('Filter findings linked to a specific signal id'),
  findingIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    )
    .describe('Filter findings by a specific list of finding IDs'),
  endpointId: z.coerce
    .string()
    .optional()
    .describe('Filter findings by a specific endpoint id'),
  entryPointId: z.coerce
    .string()
    .optional()
    .describe('Filter findings by signals belonging to a specific entry point'),
  cweIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    ),
  owaspCategory: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    ),
  isPublished: OptionalBooleanQueryParamSchema,
  includeUnpublished: OptionalBooleanQueryParamSchema.describe(
    'Include unpublished findings (requires Execute Tests permission)',
  ),
  hasPrevention: OptionalBooleanQueryParamSchema,
  hasPendingSeverityChangeRequest: OptionalBooleanQueryParamSchema,
  wasAutoPublished: OptionalBooleanQueryParamSchema.describe(
    'Filter findings whose latest PUBLISHED audit log was performed by a SYSTEM actor',
  ),
  sortBy: FindingsSortBySchema.optional().describe(
    'Sort field for the findings list',
  ),
  sortOrder: z
    .enum(['asc', 'desc'])
    .optional()
    .describe('Sort order for sortBy'),
  ...FindingsThenSortQueryShape,
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
  discoveryStartDate: z.coerce.date().optional(),
  discoveryEndDate: z.coerce.date().optional(),
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
  lifecycleState: LifecycleStateFilterSchema.optional(),
  targetType: z
    .union([TargetTypeSchema, z.array(TargetTypeSchema)])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    )
    .describe('Filter findings by one or more target types'),
  targetScope: FindingTargetFilterQuerySchema.shape.targetScope,
}).transform((query) => {
  const {
    createdAtFrom,
    createdAtTo,
    searchTerm,
    assetSearch,
    assetIds,
    skip,
    limit,
    groupBy,
    ...rest
  } = query;
  const maxLimit = groupBy
    ? FINDINGS_GROUPED_LIST_LIMIT
    : PaginationDefaults.LIMIT;

  return {
    ...rest,
    groupBy,
    skip: skip ?? 0,
    limit: Math.min(limit ?? PaginationDefaults.LIMIT, maxLimit),
    searchTerm: searchTerm?.trim() || undefined,
    assetSearch: assetSearch?.trim() || undefined,
    assetIds: assetIds?.filter((id) => id.length > 0),
    findingIds: query.findingIds?.filter((id) => id.length > 0),
    signalId: query.signalId?.trim() || undefined,
    createdStartDate: query.createdStartDate ?? createdAtFrom,
    createdEndDate: query.createdEndDate ?? createdAtTo,
  };
});

export const FindingLinkedAssetCountsQuerySchema = z.object({
  targetId: z.string(),
  targetType: TargetTypeSchema,
  includeUnpublished: OptionalBooleanQueryParamSchema.describe(
    'Include unpublished findings (requires Execute Tests permission)',
  ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter findings and linked assets by lifecycle (active, archived, or all)',
  ),
});

export const FindingLinkedAssetCountsResponseSchema = z.object({
  counts: z.record(z.string(), z.number()),
});

export const FindingsListResponseSchema = z.object({
  data: z.array(FindingListEnrichedDtoSchema),
  total: z.number(),
  groups: z.array(FindingsGroupCountSchema).default([]),
});

// Type exports
export type FindingListDto = z.infer<typeof FindingListDtoSchema>;
export type FindingDetailDto = z.infer<typeof FindingDetailDtoSchema>;
export type FindingListEnrichedDto = z.infer<
  typeof FindingListEnrichedDtoSchema
>;
export type FindingDetailEnrichedDto = z.infer<
  typeof FindingDetailEnrichedDtoSchema
>;
export type TimelineEndpointAsset = z.infer<typeof TimelineEndpointAssetSchema>;
export type TimelineWebpageAsset = z.infer<typeof TimelineWebpageAssetSchema>;
export type TimelineDiscoveredAsset = z.infer<
  typeof TimelineDiscoveredAssetSchema
>;
export type TimelineAsset = z.infer<typeof TimelineAssetSchema>;
